import { Component, EventEmitter, Input, OnChanges, OnDestroy, Output, SimpleChanges } from '@angular/core';
import { ActionCommandService, ProgressInfo } from 'src/app/services/action-command.service';

// Reusable "what happened to this ESB command" panel. Give it a messageId
// (returned by ActionCommandService.sendCommand / sendCommand2) and it polls
// GET .../messages/{messageId}/progress every `pollIntervalMs` until the
// command completes, fails, or errors, then renders commandResult as
// pretty-printed JSON.
@Component({
    selector: 'app-message-result',
    templateUrl: './message-result.component.html',
    styleUrls: ['./message-result.component.scss'],
    standalone: false
})
export class MessageResultComponent implements OnChanges, OnDestroy {
  @Input() messageId: string | null = null;
  @Input() pollIntervalMs: number = 10000;
  @Input() maxPollDurationMs: number = 120000;
  // leaner default for list contexts (e.g. the notification bell) — the
  // status row is shown either way, this only gates the result body
  @Input() expanded: boolean = false;
  // fires once, the moment status first becomes 'completed' — lets a
  // consumer (e.g. the bell) auto-close once it's seen a successful result
  @Output() completed = new EventEmitter<void>();

  public progress: ProgressInfo | null = null;
  public loading: boolean = false;
  public timedOut: boolean = false;

  private pollToken: ReturnType<typeof setTimeout> | null = null;
  private stopped: boolean = true;
  private pollStartedAt: number = 0;

  constructor(private actionCommandService: ActionCommandService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (!changes['messageId']) return;
    this.stopPolling();
    this.progress = null;
    this.timedOut = false;
    if (this.messageId) this.startPolling();
  }

  ngOnDestroy(): void {
    this.stopPolling();
  }

  public toggleExpanded(): void {
    this.expanded = !this.expanded;
  }

  public get status(): 'running' | 'completed' | 'failed' | 'timedOut' {
    const info = this.progress?.progressInfo;
    if (this.progress && !this.progress.isSuccess) return 'failed';
    if (!info) return this.timedOut ? 'timedOut' : 'running';
    if (info.isFailure || !!info.exception) return 'failed';
    if (info.isCompleted) return 'completed';
    return this.timedOut ? 'timedOut' : 'running';
  }

  public get displayLabel(): string {
    if (this.progress?.progressInfo?.messageType) return this.progress.progressInfo.messageType;
    // no response yet — don't flash the raw guid while still loading; only
    // fall back to it once a response has actually come back without a type
    return this.progress ? this.messageId ?? '' : '';
  }

  public get resultJson(): string | null {
    const raw = this.progress?.progressInfo?.commandResult;
    if (!raw) return null;
    try {
      return JSON.stringify(JSON.parse(raw), null, 2);
    } catch {
      return raw;
    }
  }

  private startPolling(): void {
    this.stopped = false;
    this.pollStartedAt = Date.now();
    this.poll();
  }

  private async poll(): Promise<void> {
    this.loading = !this.progress;
    const messageId = this.messageId;
    const progress = await this.actionCommandService.getCommandProgress(messageId);
    if (this.stopped || this.messageId !== messageId) return;

    this.progress = progress;
    this.loading = false;

    const info = progress?.progressInfo;
    const done = !progress.isSuccess || !!info?.isCompleted || !!info?.exception || !!info?.isFailure;
    if (done) {
      if (this.status === 'completed') this.completed.emit();
      return;
    }

    if (Date.now() - this.pollStartedAt >= this.maxPollDurationMs) {
      this.timedOut = true;
      return;
    }

    this.pollToken = setTimeout(() => this.poll(), this.pollIntervalMs);
  }

  private stopPolling(): void {
    this.stopped = true;
    if (this.pollToken) {
      clearTimeout(this.pollToken);
      this.pollToken = null;
    }
  }
}
